import React, { useEffect, useState, useRef, useContext } from "react";
import { FilterContext } from "@qlik/qlik/src/context/FilterContext";
import { handleKeyboardEventBehavior } from "@qlik/qlik/src/lib/util/helpers";

/**
 * DropdownFilterGroup is a React component that renders a dropdown filter group.
 *
 * @component
 * @param {Object} props - The properties that define the DropdownFilterGroup component.
 * @param {string} props.filterName - The name of the filter.
 * @param {string} props.parentCategory - The name of the parent category.
 * @param {Array} props.categoryData - The data for the categories.
 * @param {Object} props.allParams - The parameters for all filters.
 * @param {Function} props.setAllParams - The function to set all parameters.
 *
 * @example
 * <DropdownFilterGroup filterName="exampleFilter" parentCategory="exampleCategory" categoryData={exampleData} allParams={exampleParams} setAllParams={setExampleParams} />
 *
 * @returns {React.Element} The DropdownFilterGroup component.
 */
const DropdownFilterGroup = ({
    filterName,
    parentCategory,
    categoryData,
    allParams,
    setAllParams,
    setCurrentCategory = () => {},
}) => {
    const handleCheckboxChange = (event, filterValue) => {
        setCurrentCategory(filterName);
        const checkedBoxes = allParams?.[filterName] ? [...allParams[filterName]] : [];
        if (event.target.checked) {
            checkedBoxes.push(filterValue);
        } else {
            const index = checkedBoxes.findIndex((ch) => ch === filterValue);
            checkedBoxes.splice(index, 1);
        }
        setAllParams({ ...allParams, [filterName]: checkedBoxes });
    };

    const isChecked = (filterValue) => allParams[filterName]?.includes(filterValue);
    const [dropdownVisibility, setDropdownVisibility] = useState(false);
    const dropdownRef = useRef();

    const handleDropdownVisibility = () => setDropdownVisibility(!dropdownVisibility);
    const handleDropdownLeave = (e) =>
        e.target !== dropdownRef.current && e.target.closest(".active-filter") === null && setDropdownVisibility(false);
    const handleClickPrevent = () => window.removeEventListener("click", handleDropdownLeave);

    useEffect(() => {
        window.addEventListener("click", handleDropdownLeave);
        return () => {
            window.removeEventListener("click", handleDropdownLeave);
        };
    }, []);

    const checkboxRef = useRef();
    const validFilters = useContext(FilterContext);

    return (
        <div className="w-full h-15 border border-stone rounded cursor-pointer">
            <p
                className={`relative body py-4 px-5 filter-dropdown-arrow ${dropdownVisibility ? "active" : ""}`}
                role="button"
                onClick={handleDropdownVisibility}
                tabIndex={-1}
                onKeyDown={(e) => {
                    handleKeyboardEventBehavior(e, handleDropdownVisibility);
                }}
                ref={dropdownRef}
            >
                {parentCategory}
            </p>
            <div
                className={`dropdown-wrapper relative z-1 mt-2 py-4 px-5 border border-stone rounded bg-white transition-opacity duration-500 ${
                    !dropdownVisibility ? "opacity-0 pointer-events-none" : "opacity-1 active-filter"
                }`}
            >
                {categoryData?.map((filter) => {
                    const disabled =
                        (Object.keys(validFilters).length &&
                            !validFilters[filter.title] &&
                            !checkboxRef.current?.checked) ||
                        filter.isDisabled;
                    return (
                        <button key={filter.id} className="relative flex gap-2" onClick={handleClickPrevent}>
                            <label
                                htmlFor={filter.id}
                                className={`body pl-8 mb-2 text-base font-light ${
                                    disabled ? "opacity-20 cursor-default" : "cursor-pointer"
                                }`}
                            >
                                {filter.title}
                                <input
                                    className={`absolute h-0 w-0 opacity-0 ${
                                        disabled ? "cursor-default" : "cursor-pointer"
                                    }`}
                                    id={filter.id}
                                    type="checkbox"
                                    value={filter.title}
                                    checked={isChecked(filter.title)}
                                    onChange={(e) => handleCheckboxChange(e, filter.title)}
                                    disabled={disabled}
                                />
                                <span
                                    className={`absolute left-0 top-0.5 w-5 h-5 border rounded-xxs transition duration-300 ${
                                        isChecked(filter.title)
                                            ? "bg-green border-green bg-tick bg-no-repeat bg-center"
                                            : "border-stone"
                                    }`}
                                />
                            </label>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default DropdownFilterGroup;
